import useSWR from 'swr'
import { useEffect, useState } from 'react'
import { useAppStore } from '@forwardflow/store/appStore'
import {
   gated,
   isFreeFlow,
} from '@forwardflow/features/campaign-v2/utils/demo-mode'
import { campaignV2Api } from '../services'
import { ICriteria, TContentKind } from '../interfaces/campaign-v2.types'

const K = 'CAMPAIGN_V2'

/** Demo data is fine without an org; the live BFF is not. */
const useKey = (parts: unknown[] | null): unknown[] | null => {
   const org = useAppStore((s) => s.currentOrganization)
   return !parts
      ? null
      : isFreeFlow()
        ? [...parts, 'demo']
        : org
          ? gated([...parts, org])
          : null
}

export const useV2Readiness = () =>
   useSWR(useKey([K, 'readiness']), () => campaignV2Api().readiness(), {
      shouldRetryOnError: false,
   })

export const useBusinessCategories = () =>
   useSWR(
      useKey([K, 'categories']),
      () => campaignV2Api().businessCategories(),
      {
         revalidateOnFocus: false,
      }
   )

export const useCatalog = (kind: TContentKind, categoryId: number | null) =>
   useSWR(
      useKey(categoryId ? [K, 'catalog', kind, categoryId] : null),
      () => campaignV2Api().catalog(kind, categoryId as number),
      { revalidateOnFocus: false }
   )

export const useVoices = () =>
   useSWR(useKey([K, 'voices']), () => campaignV2Api().voices(), {
      revalidateOnFocus: false,
   })

export const useAgents = () =>
   useSWR(useKey([K, 'agents']), () => campaignV2Api().agents(), {
      revalidateOnFocus: false,
   })

export const useLiveCampaign = (campaignId: string | null) =>
   useSWR(useKey(campaignId ? [K, 'live', campaignId] : null), () =>
      campaignV2Api().readCampaign(campaignId as string)
   )

export const usePreview = (campaignId: string | null, revision: number) =>
   useSWR(
      useKey(campaignId ? [K, 'preview', campaignId, revision] : null),
      () => campaignV2Api().preview(campaignId as string),
      { revalidateOnFocus: false }
   )

export const useInventoryNumbers = () =>
   useSWR(useKey([K, 'inventory']), () => campaignV2Api().inventoryNumbers())

export const useAvailableNumbers = (search: string | null) =>
   useSWR(
      useKey(search === null ? null : [K, 'available', search]),
      () => campaignV2Api().availableNumbers(search || ''),
      { revalidateOnFocus: false, shouldRetryOnError: false }
   )

export const useNumberPricing = (total: number) =>
   useSWR(
      useKey(total > 0 ? [K, 'pricing', total] : null),
      () => campaignV2Api().pricing(total),
      {
         revalidateOnFocus: false,
      }
   )

export const useOrgAnswering = () =>
   useSWR(useKey([K, 'answering']), () => campaignV2Api().orgAnswering(), {
      revalidateOnFocus: false,
   })

export const useEstimatedLeads = (
   criteria: ICriteria[] | null,
   campaignId: string | null = null,
   revision = 0
) => {
   const scopedKey = useKey(criteria ? [K, 'estimate', criteria, campaignId, revision] : null)
   const requestKey = scopedKey ? JSON.stringify(scopedKey) : null
   const [settledKey, setSettledKey] = useState<string | null>(null)
   useEffect(() => {
      const timer = window.setTimeout(() => setSettledKey(requestKey), 500)
      return () => window.clearTimeout(timer)
   }, [requestKey])
   // Disable the previous key immediately when filters/org/campaign change.
   // SWR keeps in-flight results under their own key, never the new audience.
   const pending = requestKey !== settledKey
   const result = useSWR(
      !pending ? settledKey : null,
      (key: string) => {
         const [, , selectedCriteria, selectedCampaign] = JSON.parse(key)
         return campaignV2Api().estimatedLeads(selectedCriteria, selectedCampaign)
      },
      {
         revalidateOnFocus: false,
         revalidateOnReconnect: false,
         revalidateIfStale: true,
         shouldRetryOnError: false,
         keepPreviousData: false,
         dedupingInterval: 5000,
      }
   )
   return { ...result, data: pending ? undefined : result.data, isValidating: pending || result.isLoading || result.isValidating }
}
